// hooks/useProducts.js
import { useCallback, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { readSubcollectionValues } from '@/utils/firestoreSubcollections';

export default function useProducts(db, catMap) {
  const [products, setProducts] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);

  const baseQuery = useCallback(
    (subCategoryId = null) => {
      const base = [
        collection(db, 'marketplace'),
        where('type', '==', 'products'),
        orderBy('createdAt', 'desc'),
        limit(10),
      ];
      if (subCategoryId) {
        return query(
          collection(db, 'marketplace'),
          where('type', '==', 'products'),
          where('subCategoryId', '==', subCategoryId),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
      }
      return query(...base);
    },
    [db]
  );

  const hydrate = useCallback(
    async (snap) => {
      const list = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data();
          const [breedsArr, geoArr, tagsArr] = await Promise.all([
            readSubcollectionValues(db, d.id, 'breeds'),
            readSubcollectionValues(db, d.id, 'geography'),
            readSubcollectionValues(db, d.id, 'tags'),
          ]);
          return {
            id: d.id,
            ...data,
            categoryName:
              (data.categoryId && catMap[data.categoryId]?.name) || '',
            subCategoryName:
              (data.subCategoryId && catMap[data.subCategoryId]?.name) || '',
            breeds: breedsArr,
            geography: geoArr,
            tags: tagsArr,
          };
        })
      );
      return list;
    },
    [db, catMap]
  );

  const fetchProducts = useCallback(
    async (subCategoryId = null) => {
      setLoading(true);
      try {
        const snap = await getDocs(baseQuery(subCategoryId));
        setProducts(await hydrate(snap));
        setLastVisible(snap.docs[snap.docs.length - 1] || null);
      } finally {
        setLoading(false);
      }
    },
    [baseQuery, hydrate]
  );

  const fetchMore = useCallback(async () => {
    if (!lastVisible) return;
    const qRef = query(
      collection(db, 'marketplace'),
      where('type', '==', 'products'),
      orderBy('createdAt', 'desc'),
      startAfter(lastVisible),
      limit(10)
    );
    const snap = await getDocs(qRef);
    const more = await hydrate(snap);
    setProducts((prev) => [...prev, ...more]);
    setLastVisible(snap.docs[snap.docs.length - 1] || null);
  }, [db, lastVisible, hydrate]);

  return {
    products,
    loading,
    lastVisible,
    fetchProducts,
    fetchMore,
    setProducts,
    setLoading,
  };
}
